import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  MessagesSquare, Search, Send, FileText, UserRound, UserCog, Bot, Lock, Loader2, MessageSquareText, ArrowLeft, Plus, X, Check, CheckCheck, AlertCircle, Download, CheckSquare, Square, Clock, MousePointerClick, Sparkles, RefreshCw, Ban,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChatTags } from "@/components/ChatTags";
import { FormSendDialog } from "@/components/forms/FormSendDialog";
import { ClipboardList } from "lucide-react";
import { ChatPauseControl } from "@/components/ChatPauseControl";
import { OriginCaption, originMeta } from "@/components/OriginCaption";
import { QuickRepliesPicker, QuickRepliesManager, SlashSuggestions } from "@/components/QuickReplies";

function ChatSummary({ phone, lastTime }) {
  const [state, setState] = useState({ text: null, loading: true });
  const [expanded, setExpanded] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  const load = (refresh = false) => {
    setState((s) => ({ ...s, loading: true }));
    api.get(`/chat/${encodeURIComponent(phone)}/summary`, { params: refresh ? { refresh: true } : {} })
      .then((r) => setState({ text: r.data.text, loading: false, messages: r.data.messages }))
      .catch(() => setState({ text: null, loading: false, error: true }));
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, [phone, lastTime]);
  if (!state.loading && !state.text && !state.error) return null;
  return (
    <div className="px-4 py-2 border-b border-[#A78BFA]/30 bg-[#A78BFA]/10 flex items-start gap-2 text-xs" data-testid="chat-summary-banner">
      <Sparkles className="h-3.5 w-3.5 text-[#A78BFA] shrink-0 mt-0.5" />
      <p className={`flex-1 text-slate-200 leading-snug ${expanded ? "" : "line-clamp-1"}`} data-testid="chat-summary-text">
        {state.loading ? <span className="text-slate-400">Claude sta riassumendo la conversazione…</span> : state.error ? <span className="text-slate-400">Riassunto non disponibile.</span> : state.text}
      </p>
      {!state.loading && state.text && (
        <button type="button" onClick={() => setExpanded((e) => !e)} data-testid="chat-summary-expand"
                className="shrink-0 text-[#A78BFA] hover:text-[#C4B5FD] underline-offset-2 hover:underline">
          {expanded ? "riduci" : "mostra"}
        </button>
      )}
      <button type="button" onClick={() => load(true)} disabled={state.loading} data-testid="chat-summary-refresh" title="Aggiorna riassunto"
              className="shrink-0 text-[#A78BFA] hover:text-[#C4B5FD] disabled:opacity-50">
        {state.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}


export default function Chat() {
  const location = useLocation();
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [mode, setMode] = useState("free");
  const [freeText, setFreeText] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickPhone, setQuickPhone] = useState("");
  const [waConfigured, setWaConfigured] = useState(false);
  const [liveTemplates, setLiveTemplates] = useState([]);
  const [varValues, setVarValues] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [threadQuery, setThreadQuery] = useState("");
  const [originFilter, setOriginFilter] = useState("all");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [exportingMulti, setExportingMulti] = useState(false);
  const [closingOnly, setClosingOnly] = useState(false);
  const [labelFilter, setLabelFilter] = useState("");
  const [quickReplies, setQuickReplies] = useState([]);
  const [qrManageOpen, setQrManageOpen] = useState(false);
  const [formSendOpen, setFormSendOpen] = useState(false);
  const threadRef = useRef(null);
  const loadQuickReplies = () => api.get("/chat/quick-replies").then((r) => setQuickReplies(r.data || [])).catch(() => {});
  useEffect(() => { loadQuickReplies(); }, []);
  const onTagsChanged = (tags) => {
    setActive((a) => (a ? { ...a, tags } : a));
    setContacts((list) => list.map((c) => (c.phone === active?.phone ? { ...c, tags } : c)));
  };
  const resetGuard = async () => {
    if (!active) return;
    try {
      await api.post(`/chat/${encodeURIComponent(active.phone)}/reset-guard`);
      setActive((a) => (a ? { ...a, guard_blocked: false } : a));
      setContacts((list) => list.map((c) => (c.phone === active.phone ? { ...c, guard_blocked: false } : c)));
      toast.success("Freno sbloccato: le automazioni ripartono subito per questo cliente");
    } catch { toast.error("Operazione non riuscita"); }
  };
  const stopAutomations = async () => {
    if (!active || !window.confirm("Fermare subito tutte le automazioni (sessioni e attese programmate) per questo cliente?")) return;
    try {
      const { data } = await api.post(`/chat/${encodeURIComponent(active.phone)}/stop-automations`);
      toast.success(`Automazioni fermate: ${data.sessions} sessioni e ${data.delays} attese eliminate`);
    } catch { toast.error("Operazione non riuscita"); }
  };
  const onStatusChanged = (data) => {
    const patch = data.status === "paused"
      ? { status: "paused", paused_until: data.paused_until, unsubscribed_at: null, unsubscribed_via: null, unsub_reason: null }
      : { status: "subscribed", paused_until: null, unsubscribed_at: null, unsubscribed_via: null, unsub_reason: null };
    setActive((a) => (a ? { ...a, ...patch } : a));
    setContacts((list) => list.map((c) => (c.phone === active?.phone ? { ...c, ...patch } : c)));
  };

  const hoursLeft = (c) => {
    if (!c.is_open || !c.window_closes_at) return null;
    return Math.max(0, (new Date(c.window_closes_at) - Date.now()) / 36e5);
  };
  const fmtLeft = (h) => (h < 1 ? `${Math.max(1, Math.round(h * 60))} min` : `${Math.floor(h)}h`);
  const fmtAgo = (iso) => { const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000)); return m < 60 ? `${m} min fa` : `${Math.floor(m / 60)}h fa`; };
  const dismissHotClick = async (phone) => {
    try { await api.post(`/chat/${encodeURIComponent(phone)}/hot-click/dismiss`); loadContacts(); } catch { /* ignore */ }
  };
  const [quickReplying, setQuickReplying] = useState(false);
  const quickReplyHotClick = async (phone) => {
    setQuickReplying(true);
    try {
      await api.post(`/chat/${encodeURIComponent(phone)}/hot-click/reply`);
      toast.success("Risposta rapida inviata");
      await refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setQuickReplying(false); }
  };

  const toggleSelected = (phone) => setSelected((s) => (s.includes(phone) ? s.filter((p) => p !== phone) : [...s, phone]));
  const exitSelectMode = () => { setSelectMode(false); setSelected([]); };
  const exportSelected = async () => {
    if (!selected.length) { toast.error("Seleziona almeno una conversazione"); return; }
    setExportingMulti(true);
    try {
      const res = await api.post("/chat/export-multi", { phones: selected }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `chat_multi_${selected.length}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${selected.length} conversazioni esportate in un unico PDF`);
      exitSelectMode();
    } catch (e) {
      toast.error("Esportazione non riuscita");
    } finally {
      setExportingMulti(false);
    }
  };

  // Scorri in fondo solo all'apertura o quando arriva un messaggio nuovo (non a ogni polling: altrimenti chi legge la storia viene riportato giù)
  const lastMsgKey = useRef("");
  useEffect(() => {
    const el = threadRef.current;
    const key = `${active?.phone || ""}|${thread.length}|${thread[thread.length - 1]?.id || ""}`;
    if (el && key !== lastMsgKey.current) {
      lastMsgKey.current = key;
      el.scrollTop = el.scrollHeight;
    }
  }, [thread, active]);

  // Indicatore "messaggi precedenti": visibile quando il thread non è in cima (su mobile la storia resta fuori vista)
  const [scrolledDown, setScrolledDown] = useState(false);
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    const onScroll = () => setScrolledDown(el.scrollTop > 40 && el.scrollHeight > el.clientHeight + 40);
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [active, thread]);

  const loadContacts = async (retry = true) => {
    try {
      const { data } = await api.get("/chat/contacts", { params: search ? { search } : {} });
      setContacts(data);
      return data;
    } catch {
      // Un drop transitorio della connessione non deve mostrare un errore: un tentativo silenzioso prima del toast
      if (retry) {
        await new Promise((r) => setTimeout(r, 1500));
        return loadContacts(false);
      }
      toast.error("Errore nel caricamento contatti");
      return [];
    }
  };

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/whatsapp/status").then((r) => {
      setWaConfigured(!!r.data?.configured);
      if (r.data?.configured) {
        api.get("/whatsapp/live-templates")
          .then((lt) => setLiveTemplates((lt.data || []).filter((t) => t.status === "APPROVED")))
          .catch(() => {});
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Polling lista conversazioni: nuovi messaggi in arrivo compaiono senza ricaricare la pagina
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      api.get("/chat/contacts", { params: search ? { search } : {} }).then((r) => setContacts(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(id);
  }, [search]);

  // open chat from Rubrica shortcut (?phone=)
  useEffect(() => {
    const p = new URLSearchParams(location.search).get("phone");
    if (p) loadContacts().then((list) => {
      const c = list.find((x) => x.phone === p) || { phone: p, name: p, is_open: false };
      openChat(c);
    });
    // eslint-disable-next-line
  }, [location.search]);

  const openChat = async (c) => {
    setActive(c);
    setMode(c.is_open ? "free" : "template");
    try {
      const { data } = await api.get(`/whatsapp/conversations/${encodeURIComponent(c.phone)}`);
      setThread(data);
    } catch {
      setThread([]);
      toast.error("Errore nel caricamento della conversazione");
    }
  };

  const startQuickChat = () => {
    const phone = quickPhone.trim();
    if (!phone) { toast.error("Inserisci un numero"); return; }
    openChat({ phone, name: phone, is_open: false, has_conversation: false });
    setQuickOpen(false);
    setQuickPhone("");
  };

  const refresh = async () => {
    if (active) {
      const { data } = await api.get(`/whatsapp/conversations/${encodeURIComponent(active.phone)}`);
      setThread(data);
    }
    const list = await loadContacts();
    if (active) {
      const upd = list.find((x) => x.phone === active.phone);
      if (upd) setActive(upd);
    }
  };

  // Polling leggero: aggiorna lo stato di consegna/lettura mentre la chat è aperta
  useEffect(() => {
    if (!active) return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/whatsapp/conversations/${encodeURIComponent(active.phone)}`);
        setThread(data);
      } catch (e) { /* ignora errori di polling */ }
    }, 5000);
    return () => clearInterval(id);
  }, [active]);

  const exportChat = async () => {
    if (!active) return;
    try {
      const res = await api.get(`/chat/${encodeURIComponent(active.phone)}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chat_${active.phone.replace(/[^0-9A-Za-z]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Conversazione esportata in PDF");
    } catch (e) {
      toast.error("Esportazione non riuscita");
    }
  };

  const send = async () => {
    if (!active) return;
    if (mode === "free" && !freeText.trim()) { toast.error("Scrivi un messaggio"); return; }
    if (mode === "template" && !templateId) { toast.error("Seleziona un template"); return; }
    if (mode === "template" && usingLive && varValues.some((v) => !v || !v.trim())) {
      toast.error("Compila tutte le variabili del template"); return;
    }
    if (mode === "template" && active.status === "unsubscribed" &&
        !window.confirm("Questo cliente si è DISISCRITTO dalle comunicazioni. Inviare comunque il template? (Solo per comunicazioni di servizio, mai promozionali)")) return;
    setSending(true);
    try {
      let payload;
      if (mode === "template") {
        payload = usingLive
          ? { mode: "template", meta_template_name: liveSel.name, meta_template_language: liveSel.language,
              body_text: liveSel.body_text, variable_values: varValues }
          : { mode: "template", template_id: templateId };
      } else {
        payload = { mode: "free", text: freeText };
      }
      await api.post(`/chat/send/${encodeURIComponent(active.phone)}`, payload);
      toast.success(mode === "template" ? "Template inviato" : "Messaggio inviato");
      setFreeText("");
      // Il messaggio e' gia' partito: un errore nel ricaricare la lista non deve apparire come invio fallito
      await refresh().catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const LABELS = { urgente: ["Urgente", "bg-[#EF4444]/15 text-[#F87171]"], reclamo: ["Reclamo", "bg-[#F97316]/15 text-[#FB923C]"], vendita: ["Vendita", "bg-[#10B981]/15 text-[#10B981]"], info: ["Info", "bg-[#38BDF8]/15 text-[#38BDF8]"] };
  const byClosing = closingOnly ? contacts.filter((c) => hoursLeft(c) !== null && hoursLeft(c) <= 3) : contacts;
  const filtered = labelFilter ? byClosing.filter((c) => c.label === labelFilter) : byClosing;
  const labelCounts = contacts.reduce((acc, c) => { if (c.label) acc[c.label] = (acc[c.label] || 0) + 1; return acc; }, {});
  const closingSoon = contacts.filter((c) => hoursLeft(c) !== null && hoursLeft(c) <= 3);
  const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const matchOrigin = (m) => {
    if (originFilter === "all") return true;
    const t = m.origin?.type;
    if (originFilter === "automation") return t === "automation" || t === "wait" || (m.action === "automation" && m.ai_reply);
    if (originFilter === "campaign") return t === "campaign";
    if (originFilter === "operator") return !!m.operator_reply && m.action !== "system" && m.kind !== "campaign";
    if (originFilter === "ai") return !!m.ai_reply && !t && m.action !== "automation";
    if (originFilter === "customer") return !!m.message;
    return true;
  };
  const visibleThread = thread.filter(matchOrigin).filter((m) => !threadQuery.trim()
    || `${m.message || ""} ${m.operator_reply || ""} ${m.ai_reply || ""}`.toLowerCase().includes(threadQuery.trim().toLowerCase()));
  const dayKey = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; };
  const dayLabel = (iso) => {
    const d = new Date(iso); const n = new Date();
    const a = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const b = new Date(n.getFullYear(), n.getMonth(), n.getDate());
    const diff = Math.round((b - a) / 86400000);
    if (diff === 0) return "Oggi";
    if (diff === 1) return "Ieri";
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  };
  const usingLive = waConfigured && liveTemplates.length > 0;
  const liveSel = usingLive ? liveTemplates.find((t) => `${t.name}|${t.language}` === templateId) : null;
  const localSel = !usingLive ? templates.find((t) => t.id === templateId) : null;
  const varCount = liveSel ? (liveSel.variables || 0) : 0;
  const templateBody = liveSel ? (liveSel.body_text || "") : (localSel ? (localSel.body || "") : "");
  const previewText = liveSel
    ? varValues.reduce((acc, v, i) => acc.replaceAll(`{{${i + 1}}}`, v || `{{${i + 1}}}`), templateBody)
    : (templateBody || "").replaceAll("{{nome}}", active?.name || "cliente");

  const selectTemplate = (val) => {
    setTemplateId(val);
    const lt = usingLive ? liveTemplates.find((t) => `${t.name}|${t.language}` === val) : null;
    const n = lt ? (lt.variables || 0) : 0;
    setVarValues(Array.from({ length: n }, (_, i) => (i === 0 ? (active?.name || "") : "")));
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-100">Chat</h1>
        <p className="text-slate-400 text-sm mt-1">Invia singolarmente un messaggio libero o un template a ogni cliente</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 bg-[#111827]/80 border border-[#1F2937] rounded-2xl overflow-hidden">
        {/* Names column */}
        <div className={`lg:col-span-4 border-b lg:border-b-0 lg:border-r border-[#1F2937] flex-col h-[calc(100dvh-13rem)] lg:h-[680px] ${active ? "hidden lg:flex" : "flex"}`}>
          <div className="px-4 py-3 border-b border-[#1F2937] space-y-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input data-testid="chat-contact-search" value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder="Cerca per nome o numero..." className="pl-9 h-9 bg-[#0A0E17] border-[#1F2937] text-slate-100" />
              </div>
              <Button type="button" data-testid="chat-select-mode-button" onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                      title="Seleziona più conversazioni da esportare"
                      className={`h-9 px-3 shrink-0 font-semibold ${selectMode ? "bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0E17]" : "bg-[#1E293B] hover:bg-[#334155] text-slate-200"}`}>
                <CheckSquare className="h-4 w-4" />
              </Button>
              <Button type="button" data-testid="chat-new-quick-button" onClick={() => setQuickOpen((v) => !v)}
                      className="h-9 px-3 bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold shrink-0">
                {quickOpen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>
            {selectMode && (
              <div className="flex items-center gap-2 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30 px-2.5 py-1.5" data-testid="chat-multi-export-bar">
                <span className="text-xs text-[#F59E0B] flex-1" data-testid="chat-selected-count">{selected.length} selezionate</span>
                <button type="button" data-testid="chat-select-all" onClick={() => {
                  const withConv = filtered.filter((c) => c.has_conversation || c.is_open).map((c) => c.phone);
                  setSelected(withConv.slice(0, 50));
                  if (withConv.length > 50) toast.info("Selezionate le prime 50 conversazioni (limite per esportazione)");
                }}
                        className="text-[11px] text-slate-300 hover:text-white">Tutte</button>
                <Button type="button" size="sm" data-testid="chat-export-multi-button" onClick={exportSelected} disabled={exportingMulti || !selected.length}
                        className="h-7 px-2.5 bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold text-xs">
                  {exportingMulti ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Download className="h-3.5 w-3.5 mr-1" /> Esporta PDF</>}
                </Button>
              </div>
            )}
            {quickOpen && (
              <div className="flex items-center gap-2" data-testid="chat-quick-row">
                <Input data-testid="chat-quick-phone" value={quickPhone} onChange={(e) => setQuickPhone(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") startQuickChat(); }}
                       placeholder="+39 333 1234567" className="h-9 bg-[#0A0E17] border-[#1F2937] text-slate-100" />
                <Button type="button" data-testid="chat-quick-start" onClick={startQuickChat}
                        className="h-9 px-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold shrink-0">
                  Avvia
                </Button>
              </div>
            )}
          </div>
          <div className="overflow-y-auto flex-1" data-testid="chat-contacts-list">
            {Object.keys(labelCounts).length > 0 && (
              <div className="px-3 py-2 border-b border-[#1F2937] flex flex-wrap gap-1.5" data-testid="chat-label-filters">
                <button type="button" onClick={() => setLabelFilter("")} data-testid="chat-label-filter-all"
                        className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${!labelFilter ? "bg-slate-200 text-[#0A0E17] border-slate-200" : "border-[#1F2937] text-slate-400 hover:text-slate-200"}`}>Tutte</button>
                {Object.entries(LABELS).filter(([k]) => labelCounts[k]).map(([k, [lbl, cls]]) => (
                  <button key={k} type="button" onClick={() => setLabelFilter(labelFilter === k ? "" : k)} data-testid={`chat-label-filter-${k}`}
                          className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${labelFilter === k ? `${cls} border-current` : "border-[#1F2937] text-slate-400 hover:text-slate-200"}`}>{lbl} · {labelCounts[k]}</button>
                ))}
              </div>
            )}
            {closingSoon.length > 0 && (
              <button type="button" onClick={() => setClosingOnly((v) => !v)} data-testid="chat-closing-banner"
                      className={`w-full text-left px-4 py-2 border-b flex items-center gap-2 text-xs transition-colors ${closingOnly ? "bg-[#F59E0B]/20 border-[#F59E0B]/40 text-[#F59E0B]" : "bg-[#F59E0B]/10 border-[#F59E0B]/20 text-[#F59E0B] hover:bg-[#F59E0B]/15"}`}>
                <Clock className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1">{closingSoon.length} chat {closingSoon.length === 1 ? "si chiude" : "si chiudono"} entro 3 ore: rispondi prima della scadenza</span>
                <span className="text-[10px] underline">{closingOnly ? "Mostra tutte" : "Mostra solo queste"}</span>
              </button>
            )}
            {filtered.length === 0 && <p className="text-sm text-slate-500 p-4">Nessun contatto.</p>}
            {filtered.map((c, i) => (
              <button key={`${c.phone}-${i}`} onClick={() => (selectMode ? toggleSelected(c.phone) : openChat(c))} data-testid={`chat-contact-item-${c.phone}`}
                      className={`w-full text-left px-4 py-3 border-b border-[#1F2937]/60 transition-colors ${(!selectMode && active?.phone === c.phone) || (selectMode && selected.includes(c.phone)) ? "bg-[#1F2937]/70" : "hover:bg-[#1F2937]/40"}`}>
                <div className="flex items-center gap-3">
                  {selectMode && (
                    <span data-testid={`chat-select-${c.phone}`} className={selected.includes(c.phone) ? "text-[#10B981]" : "text-slate-600"}>
                      {selected.includes(c.phone) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                    </span>
                  )}
                  <div className="h-9 w-9 rounded-full bg-[#1E293B] flex items-center justify-center text-sm font-semibold text-[#10B981] shrink-0">
                    {(c.name || c.phone).charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-200 font-medium truncate">{c.name || c.phone}</span>
                      {c.ai_paused && <Badge className="bg-[#7C3AED]/20 text-[#C4B5FD] border-0 text-[10px] shrink-0 font-semibold" data-testid={`chat-operator-badge-${c.phone}`}>Operatore</Badge>}
                      {c.label && LABELS[c.label] && (
                        <Badge className={`${LABELS[c.label][1]} border-0 text-[10px] shrink-0 font-semibold`} data-testid={`chat-label-badge-${c.phone}`}>{LABELS[c.label][0]}</Badge>
                      )}
                      {c.hot_click_at && (
                        <Badge className="bg-[#F59E0B] text-[#0A0E17] border-0 text-[10px] shrink-0 font-semibold" data-testid={`chat-hot-click-badge-${c.phone}`}>
                          Ha cliccato · {fmtAgo(c.hot_click_at)}
                        </Badge>
                      )}
                      {c.is_open
                        ? (hoursLeft(c) !== null && hoursLeft(c) <= 3
                          ? <Badge className="bg-[#F59E0B]/15 text-[#F59E0B] border-0 text-[10px] shrink-0" data-testid={`chat-closing-badge-${c.phone}`}>chiude tra {fmtLeft(hoursLeft(c))}</Badge>
                          : <Badge className="bg-[#10B981]/15 text-[#10B981] border-0 text-[10px] shrink-0" title={c.window_closes_at ? `Finestra aperta ancora ${fmtLeft(hoursLeft(c))}` : ""}>aperta{hoursLeft(c) !== null ? ` · ${fmtLeft(hoursLeft(c))}` : ""}</Badge>)
                        : c.has_conversation && <Badge className="bg-slate-500/15 text-slate-400 border-0 text-[10px] shrink-0">24h chiusa</Badge>}
                      {c.last_at && <span className="text-[10px] text-slate-500 ml-auto shrink-0 font-mono2" data-testid={`chat-last-time-${c.phone}`}>{fmtTime(c.last_at)}</span>}
                    </div>
                    <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                      {c.last_outbound && (
                        c.last_status === "read" ? <CheckCheck className="h-3 w-3 text-[#38BDF8] shrink-0" />
                        : c.last_status === "delivered" ? <CheckCheck className="h-3 w-3 text-slate-400 shrink-0" />
                        : c.last_status === "failed" ? <AlertCircle className="h-3 w-3 text-[#EF4444] shrink-0" />
                        : <Check className="h-3 w-3 text-slate-400 shrink-0" />
                      )}
                      <span className="truncate">{c.preview || c.phone}</span>
                    </p>
                  </div>
                  {c.pending && (
                    <span data-testid={`chat-unread-dot-${c.phone}`}
                          className="h-2.5 w-2.5 rounded-full bg-[#10B981] shrink-0 shadow-[0_0_8px_#10B981] animate-pulse" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Chat column */}
        <div className={`lg:col-span-8 flex-col h-[calc(100dvh-13rem)] lg:h-[680px] ${active ? "flex" : "hidden lg:flex"}`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <MessagesSquare className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Seleziona un cliente per aprire la chat.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#1F2937] bg-[#0B1120] flex items-center gap-3">
                <button type="button" onClick={() => setActive(null)} data-testid="chat-back-button"
                        className="lg:hidden -ml-1 mr-1 text-slate-400 hover:text-slate-200 transition-colors">
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="h-9 w-9 rounded-full bg-[#1E293B] flex items-center justify-center text-sm font-semibold text-[#10B981]">
                  {(active.name || active.phone).charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="text-sm text-slate-200 font-medium truncate">{active.name || active.phone}</p>
                    {active.status === "unsubscribed" && (
                      <span data-testid="chat-unsubscribed-badge" title={`Il cliente ha chiesto di non ricevere più comunicazioni${active.unsubscribed_via ? ` (via ${active.unsubscribed_via === "sms" ? "SMS" : "WhatsApp"})` : ""}. Non inviare promozioni.`}
                            className="inline-flex items-center gap-1 rounded-full bg-[#EF4444]/15 text-[#EF4444] border border-[#EF4444]/30 px-2 py-0.5 text-[10px] font-semibold shrink-0">
                        <Ban className="h-3 w-3" /> Disiscritto{active.unsubscribed_at ? ` il ${new Date(active.unsubscribed_at).toLocaleDateString("it-IT")}` : ""}{active.unsub_reason ? ` · ${active.unsub_reason}` : ""}
                      </span>
                    )}
                    {active.status === "paused" && (
                      <span data-testid="chat-paused-badge" title="Il cliente ha scelto una pausa: è escluso dalle campagne fino alla data indicata, poi torna attivo da solo."
                            className="inline-flex items-center gap-1 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30 px-2 py-0.5 text-[10px] font-semibold shrink-0">
                        <Clock className="h-3 w-3" /> In pausa{active.paused_until ? ` fino al ${new Date(active.paused_until).toLocaleDateString("it-IT")}` : ""}
                      </span>
                    )}
                    <ChatPauseControl active={active} onChanged={onStatusChanged} />
                    {active.guard_blocked && (
                      <button type="button" onClick={resetGuard} data-testid="chat-reset-guard-button"
                              title="Il freno di sicurezza ha bloccato le automazioni per questo numero (troppi invii in 1 ora). Clicca per sbloccare subito."
                              className="inline-flex items-center gap-1 rounded-full bg-[#EF4444]/15 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/25 px-2 py-0.5 text-[10px] font-semibold transition-colors shrink-0">
                        <AlertCircle className="h-3 w-3" /> Freno attivo · Sblocca
                      </button>
                    )}
                    <button type="button" onClick={stopAutomations} data-testid="chat-stop-automations-button"
                            title="Interrompe subito sessioni e attese programmate delle automazioni per questo cliente"
                            className="inline-flex items-center gap-1 rounded-full border border-dashed border-[#334155] text-slate-400 hover:text-[#EF4444] hover:border-[#EF4444] px-2 py-0.5 text-[10px] transition-colors shrink-0">
                      <Ban className="h-3 w-3" /> Ferma automazioni
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 font-mono2">{active.phone}</p>
                  <ChatTags active={active} onChanged={onTagsChanged} />
                </div>
                <button type="button" onClick={() => setShowSummary((s) => !s)} data-testid="chat-summary-toggle" title="Riassunto IA della conversazione"
                        className={`shrink-0 transition-colors ${showSummary ? "text-[#A78BFA]" : "text-slate-400 hover:text-slate-200"}`}>
                  <Sparkles className="h-4 w-4" />
                </button>
                <button type="button" onClick={exportChat} data-testid="chat-export-button" title="Esporta in PDF"
                        className="shrink-0 text-slate-400 hover:text-slate-200 transition-colors">
                  <Download className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setSearchOpen((o) => !o)} data-testid="chat-search-toggle"
                        className={`shrink-0 transition-colors ${searchOpen ? "text-[#10B981]" : "text-slate-400 hover:text-slate-200"}`}>
                  <Search className="h-4 w-4" />
                </button>
              </div>

              {searchOpen && (
                <div className="px-3 py-2 border-b border-[#1F2937] bg-[#0B1120] flex items-center gap-2">
                  <Input autoFocus value={threadQuery} onChange={(e) => setThreadQuery(e.target.value)}
                         data-testid="chat-thread-search" placeholder="Cerca nella conversazione..."
                         className="h-8 bg-[#0A0E17] border-[#1F2937] text-slate-100 text-sm" />
                  <Select value={originFilter} onValueChange={setOriginFilter}>
                    <SelectTrigger className="h-8 w-44 bg-[#0A0E17] border-[#1F2937] text-slate-100 text-xs shrink-0" data-testid="chat-origin-filter"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                      <SelectItem value="all">Tutti i messaggi</SelectItem>
                      <SelectItem value="automation">Solo automazioni</SelectItem>
                      <SelectItem value="campaign">Solo campagne</SelectItem>
                      <SelectItem value="operator">Solo operatore</SelectItem>
                      <SelectItem value="ai">Solo Assistente IA</SelectItem>
                      <SelectItem value="customer">Solo cliente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {showSummary && <ChatSummary phone={active.phone} lastTime={thread.length ? thread[thread.length - 1].time : ""} />}
              {active.hot_click_at && (
                <div className="px-4 py-2 border-b border-[#F59E0B]/40 bg-[#F59E0B]/15 flex items-center gap-2 text-xs text-[#F59E0B]" data-testid="chat-hot-click-banner">
                  <MousePointerClick className="h-4 w-4 shrink-0" />
                  <span className="flex-1">Ha appena cliccato il link{active.hot_click_campaign ? ` di «${active.hot_click_campaign}»` : ""} ({fmtAgo(active.hot_click_at)}) e la finestra 24h è aperta: scrivigli ora.</span>
                  <button type="button" onClick={() => quickReplyHotClick(active.phone)} disabled={quickReplying} data-testid="chat-hot-click-quick-reply"
                          className="shrink-0 inline-flex items-center gap-1 rounded-md bg-[#F59E0B] text-[#0A0E17] px-2.5 py-1 text-[11px] font-semibold hover:bg-[#D97706] disabled:opacity-60">
                    {quickReplying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Rispondi subito
                  </button>
                  <button type="button" onClick={() => dismissHotClick(active.phone)} data-testid="chat-hot-click-dismiss" className="text-[10px] underline shrink-0">Ignora</button>
                </div>
              )}

              <div className="relative flex-1 min-h-0 flex flex-col">
              {scrolledDown && visibleThread.length > 1 && (
                <button type="button" data-testid="chat-scroll-top-pill"
                        onClick={() => threadRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
                        className="absolute top-2 left-1/2 -translate-x-1/2 z-10 text-[11px] px-3 py-1 rounded-full bg-[#1E293B] text-slate-200 border border-[#334155] shadow-lg hover:bg-[#273449] transition-colors">
                  ↑ Messaggi precedenti dal {new Date(visibleThread[0].created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                </button>
              )}
              <div ref={threadRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0" data-testid="chat-thread-view"
                   style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='40' height='40' fill='%23E8EDF3'/%3E%3Ccircle cx='20' cy='20' r='1' fill='%231E4FD8' fill-opacity='0.08'/%3E%3C/svg%3E\")" }}>
                {thread.length === 0 && <p className="text-sm text-slate-500 text-center py-12">Nessun messaggio. Inizia tu la conversazione.</p>}
                {thread.length > 0 && visibleThread.length === 0 && <p className="text-sm text-slate-500 text-center py-12" data-testid="chat-search-empty">Nessun messaggio corrisponde a "{threadQuery}".</p>}
                {visibleThread.map((m, i) => {
                  const prev = visibleThread[i - 1];
                  const showSep = !prev || dayKey(m.created_at) !== dayKey(prev.created_at);
                  return (
                  <div key={m.id} className="space-y-2">
                    {showSep && (
                      <div className="flex justify-center my-2" data-testid={`chat-day-sep-${i}`}>
                        <span className="text-[10px] text-slate-400 bg-[#1E293B] px-2.5 py-1 rounded-full">{dayLabel(m.created_at)}</span>
                      </div>
                    )}
                    {m.message && (
                      <div className="flex items-start gap-2">
                        <div className="h-7 w-7 rounded-full bg-[#1E293B] flex items-center justify-center shrink-0"><UserRound className="h-4 w-4 text-slate-400" /></div>
                        <div className="bg-[#1E293B] rounded-2xl rounded-tl-sm px-3 py-2 max-w-[75%]">
                          <p className="text-sm text-slate-100 whitespace-pre-wrap">{m.message}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5 text-right">{fmtTime(m.created_at)}</p>
                        </div>
                      </div>
                    )}
                    {m.ai_reply && (
                      <div className="flex items-start gap-2 justify-end">
                        <div className={`rounded-2xl rounded-tr-sm px-3 py-2 max-w-[75%] ${m.needs_human ? "bg-[#F59E0B]/15 border border-[#F59E0B]/30" : "bg-[#005C4B]"}`}>
                          <div className="mb-0.5">
                            {originMeta(m.origin)
                              ? <Badge className="bg-[#8B5CF6]/20 text-[#C4B5FD] border-0 text-xs"><Bot className="h-3 w-3 mr-1" />Automazione</Badge>
                              : <Badge className="bg-[#10B981]/20 text-[#10B981] border-0 text-xs"><Bot className="h-3 w-3 mr-1" />IA</Badge>}
                          </div>
                          <p className="text-sm text-slate-50 whitespace-pre-wrap">{m.ai_reply}</p>
                          <OriginCaption origin={m.origin} id={m.id} />
                          <p className="text-[10px] text-slate-300/70 mt-0.5 text-right">{fmtTime(m.created_at)}</p>
                        </div>
                        <div className="h-7 w-7 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-[#8B5CF6]" /></div>
                      </div>
                    )}
                    {m.operator_reply && (
                      <div className="flex items-start gap-2 justify-end">
                        <div className="rounded-2xl rounded-tr-sm px-3 py-2 max-w-[75%] bg-[#005C4B]">
                          <div className="mb-0.5">
                            {m.kind === "campaign"
                              ? <Badge className="bg-[#F59E0B]/20 text-[#FBBF24] border-0 text-xs"><Send className="h-3 w-3 mr-1" />Campagna</Badge>
                              : m.action === "system"
                                ? <Badge className="bg-[#8B5CF6]/20 text-[#C4B5FD] border-0 text-xs"><Bot className="h-3 w-3 mr-1" />Invio automatico</Badge>
                                : m.kind === "template"
                                  ? <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] border-0 text-xs"><FileText className="h-3 w-3 mr-1" />Template</Badge>
                                  : <Badge className="bg-[#10B981]/20 text-[#10B981] border-0 text-xs"><UserCog className="h-3 w-3 mr-1" />Operatore</Badge>}
                          </div>
                          <p className="text-sm text-slate-50 whitespace-pre-wrap">{m.operator_reply}</p>
                          <OriginCaption origin={m.origin} id={m.id} />
                          <div className="flex items-center justify-end gap-1 mt-0.5" data-testid={`chat-meta-${m.id}`}>
                            <span className="text-[10px] text-slate-300/70">{fmtTime(m.created_at)}</span>
                            {m.real && m.delivery_status && (
                              <span title={m.delivery_status}>
                                {m.delivery_status === "read" && <CheckCheck className="h-3.5 w-3.5 text-[#38BDF8]" />}
                                {m.delivery_status === "delivered" && <CheckCheck className="h-3.5 w-3.5 text-slate-300" />}
                                {(m.delivery_status === "accepted" || m.delivery_status === "sent") && <Check className="h-3.5 w-3.5 text-slate-300" />}
                                {m.delivery_status === "failed" && <AlertCircle className="h-3.5 w-3.5 text-[#EF4444]" />}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="h-7 w-7 rounded-full bg-[#10B981]/20 flex items-center justify-center shrink-0"><UserCog className="h-4 w-4 text-[#10B981]" /></div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
              </div>

              {/* Composer */}
              <div className="border-t border-[#1F2937] p-3 bg-[#0B1120] space-y-2 shrink-0 max-h-[45vh] lg:max-h-[60vh] overflow-y-auto">
                <div className="flex gap-2">
                  <button type="button" data-testid="chat-mode-free" onClick={() => active.is_open && setMode("free")}
                          disabled={!active.is_open}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${mode === "free" ? "bg-[#10B981] text-[#0A0E17]" : "bg-[#1E293B] text-slate-400"} ${!active.is_open ? "opacity-40 cursor-not-allowed" : ""}`}>
                    <MessageSquareText className="h-3.5 w-3.5 inline mr-1" /> Messaggio libero
                  </button>
                  <button type="button" data-testid="chat-mode-template" onClick={() => setMode("template")}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${mode === "template" ? "bg-[#3B82F6] text-white" : "bg-[#1E293B] text-slate-400"}`}>
                    <FileText className="h-3.5 w-3.5 inline mr-1" /> Template
                  </button>
                  <button type="button" data-testid="chat-send-form-button" onClick={() => setFormSendOpen(true)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#1E293B] text-slate-400 hover:text-slate-200">
                    <ClipboardList className="h-3.5 w-3.5 inline mr-1" /> Form
                  </button>
                </div>

                {!active.is_open && (
                  <p className="text-[11px] text-[#F59E0B] flex items-center gap-1" data-testid="chat-window-closed-note">
                    <Lock className="h-3 w-3" /> Finestra 24h chiusa: il cliente non scrive da oltre 24h, puoi inviare solo un template.
                  </p>
                )}

                {mode === "free" ? (
                  <div className="space-y-2">
                  <SlashSuggestions text={freeText} items={quickReplies} contact={active} onPick={(t) => setFreeText(t)} />
                  <div className="flex gap-2">
                    <Input data-testid="chat-free-input" value={freeText} onChange={(e) => setFreeText(e.target.value)}
                           onKeyDown={(e) => { if (e.key === "Enter" && !freeText.startsWith("/")) send(); }} disabled={!active.is_open}
                           placeholder="Scrivi un messaggio libero... (digita / per le risposte rapide)" className="bg-[#0A0E17] border-[#1F2937] text-slate-100" />
                    <QuickRepliesPicker items={quickReplies} contact={active} disabled={!active.is_open} onPick={(t) => setFreeText(t)} onManage={() => setQrManageOpen(true)} />
                    <Button onClick={send} disabled={sending || !active.is_open} data-testid="chat-send-button"
                            className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold shrink-0">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" disabled={sending || !freeText.trim()} data-testid="chat-send-sms-button" title="Invia questo testo via SMS (SMSFactor), anche a finestra chiusa"
                            onClick={async () => { setSending(true); try { const { data } = await api.post(`/chat/${encodeURIComponent(active.phone)}/sms`, { text: freeText }); toast.success(`SMS inviato (${data.segments} SMS${data.simulated ? ", simulazione" : ""})`); setFreeText(""); await refresh().catch(() => {}); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "SMS non inviato"); } finally { setSending(false); } }}
                            className="bg-[#1E293B] border-[#1F2937] text-slate-200 shrink-0 px-3 text-xs font-semibold">
                      SMS
                    </Button>
                  </div>
                  <button type="button" disabled={suggesting || sending} data-testid="chat-suggest-reply-button"
                          onClick={async () => { setSuggesting(true); try { const { data } = await api.post(`/chat/${encodeURIComponent(active.phone)}/suggest-reply`); setFreeText(data.text || ""); toast.success("Risposta suggerita da Claude: controlla e invia"); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Suggerimento non disponibile"); } finally { setSuggesting(false); } }}
                          className="inline-flex items-center gap-1.5 text-xs text-[#A78BFA] hover:text-[#C4B5FD] disabled:opacity-50 transition-colors">
                    {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    {suggesting ? "Claude sta scrivendo…" : "Suggerisci risposta con IA"}
                  </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Select value={templateId} onValueChange={selectTemplate}>
                      <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] text-slate-100" data-testid="chat-template-select">
                        <SelectValue placeholder="Scegli un template..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                        {usingLive
                          ? liveTemplates.map((t) => (
                              <SelectItem key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
                                {t.name} ({t.language})
                              </SelectItem>))
                          : (templates.length === 0
                              ? <div className="px-3 py-2 text-xs text-slate-500">Nessun template. Creane uno nella pagina Template.</div>
                              : templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    {usingLive && (
                      <p className="text-[10px] text-[#10B981]">Invio reale via Meta (solo template approvati).</p>
                    )}
                    {varCount > 0 && (
                      <div className="space-y-1.5" data-testid="chat-template-vars">
                        <p className="text-[11px] text-slate-500">Compila le variabili del template:</p>
                        {Array.from({ length: varCount }).map((_, i) => (
                          <Input key={i} data-testid={`chat-template-var-${i}`} value={varValues[i] || ""}
                                 onChange={(e) => { const n = [...varValues]; n[i] = e.target.value; setVarValues(n); }}
                                 placeholder={`Variabile {{${i + 1}}}`}
                                 className="h-8 bg-[#0A0E17] border-[#1F2937] text-slate-100 text-sm" />
                        ))}
                      </div>
                    )}
                    {templateId && (
                      <p className="text-xs text-slate-400 bg-[#0A0E17] rounded-lg p-2 whitespace-pre-wrap max-h-24 overflow-y-auto" data-testid="chat-template-preview">
                        {previewText}
                      </p>
                    )}
                    <Button onClick={send} disabled={sending || !templateId} data-testid="chat-send-button"
                            className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold">
                      {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Invia template
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <QuickRepliesManager open={qrManageOpen} onClose={() => setQrManageOpen(false)} items={quickReplies} reload={loadQuickReplies} />
      {active && <FormSendDialog open={formSendOpen} onClose={() => setFormSendOpen(false)} contact={active} isOpenWindow={!!active.is_open} onSent={() => openChat(active)} />}
    </div>
  );
}
